import DeviceDetector from 'device-detector-js'

const isBrowser = typeof window !== 'undefined'
const deviceDetector = new DeviceDetector()
const userAgent = isBrowser ? navigator.userAgent : ''

export const isMacOS = () => {
  if (isBrowser) {
    const device = deviceDetector.parse(userAgent)
    return device.os?.name === 'Mac'
  }
  // Check for macOS in Node.js environment
  return undefined
}

// https://www.w3.org/TR/mediaqueries-4/#descdef-media-any-pointer
export const isTouchDevice = () => {
  if (!isBrowser) {
    return false
  }

  return window.matchMedia('(any-pointer: coarse)').matches
}

/**
 * The device-detector client name (e.g. "Chrome", "Mobile Safari", "Microsoft
 * Edge"). Accepts an explicit UA so it can be unit-tested with fixtures;
 * defaults to the current browser's UA.
 */
export const getBrowserName = (ua: string = userAgent): string | undefined => {
  if (!ua) {
    return undefined
  }

  return deviceDetector.parse(ua).client?.name
}

/**
 * iPadOS 13+ ships a desktop "Mac" UA that is byte-identical to macOS Safari
 * (frozen at `10_15_7` from iPadOS 26 on), so the UA alone can't tell an iPad
 * apart from a Mac. Multi-touch is the only reliable client-side discriminator
 * — real Macs have no touchscreen. `navigator.platform` is deprecated but
 * remains the sole signal that exposes the masquerade (`userAgentData.platform`
 * does not). Pre-iPadOS-13 devices never reach here — their UA still carries an
 * `iPad`/`iPhone`/`iPod` token, so they match the `iOS` branch directly.
 *
 * @see https://stackoverflow.com/questions/58019463/how-to-detect-device-name-in-safari-on-ios-13-while-it-doesnt-show-the-correct
 */
const isIpadOsMasquerade = (): boolean => {
  if (!isBrowser) {
    return false
  }

  return navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1
}

/**
 * Whether the device runs iOS or iPadOS.
 */
export const isIOS = (ua: string = userAgent): boolean => {
  if (!ua) {
    return false
  }

  const osName = deviceDetector.parse(ua).os?.name
  if (osName === 'iOS') {
    return true
  }

  return osName === 'Mac' && isIpadOsMasquerade()
}

/**
 * Whether the app is running as an installed PWA (added to the Home Screen /
 * standalone window) rather than in a regular browser tab.
 */
export const isStandalonePwa = (): boolean => {
  if (!isBrowser) {
    return false
  }

  // Chromium/Firefox and modern iOS (16.4+)
  const matchesStandalone =
    window.matchMedia?.('(display-mode: standalone)').matches ?? false
  // Legacy iOS (< 16.4)
  const iosStandalone =
    (navigator as Navigator & { standalone?: boolean }).standalone === true

  return matchesStandalone || iosStandalone
}
